const gray = {
    50: '#FFFFFF',
    100: '#F8F8F7',
    200: '#EEEEEC',
    300: '#DDDCDA',
    400: '#C0BFB9',
    500: '#9F9C93',
    600: '#79766D',
    700: '#56544E',
    800: '#403E3B',
    900: '#1D1D1F',
};

const brightpurple = {
    50: '#FAF4FB',
    100: '#F0E2F3',
    500: '#A146B8',
    600: '#87369B',
    700: '#6E2880',
    800: '#461453',
    900: '#381641',
};

const blue = {
    100: '#DBEFFA',
    300: '#8CD0F8',
    500: '#2CB1FF',
    700: '#0882C9',
    900: '#0B4A6F',
};

const green = {
    100: '#DBF5EF',
    300: '#7DE3CA',
    500: '#008866',
    700: '#075A45',
    900: '#073127',
};

const red = {
    100: '#FBDFE0',
    300: '#F7A1A4',
    500: '#FA5A60',
    600: '#ED4F54',
    700: '#D51A20',
    900: '#7C181B',
};

const orange = {
    100: '#FEEDE2',
    300: '#FFC198',
    500: '#FFA365',
    700: '#F56300',
    900: '#913F08',
};

const transparent = {
    50: '#1D1D1F0F',
    200: '#1D1D1F1F',
    300: '#1D1D1F29',
    700: '#1D1D1F8F',
};

/**
 * Croissant primitive scales, from the Figma "Primitive DS" variables. Components use the semantic
 * tokens in `croissantColors`, not these steps.
 * @link https://www.figma.com/design/anJykRaoHi8nXjqpqlynmd/Croissant-%7C-Design-System
 */
export const croissantScales = { gray, brightpurple, blue, green, red, orange, transparent };

/**
 * Croissant semantic color tokens, generated from the Figma "Semantic DS" variable export. Names mirror
 * Figma: drop `color/` and `/default`, so `color/text/gray/secondary/default` is `text.gray.secondary`;
 * any other state joins the last segment, so `color/background/brand/bold/hovered` is
 * `background.brand.boldHovered`.
 * @link https://www.figma.com/design/anJykRaoHi8nXjqpqlynmd/Croissant-%7C-Design-System
 */
export const croissantColors = {
    background: {
        base: {
            white: gray[50],
        },
        neutral: {
            subtle: gray[100],
            subtleHovered: gray[200],
            subtlePressed: gray[300],
            subtleDisabled: gray[100],
            bold: gray[900],
            boldHovered: gray[800],
            boldPressed: gray[700],
            boldDisabled: gray[300],
        },
        brand: {
            subtlestHovered: brightpurple[50],
            subtlestPressed: brightpurple[100],
            subtle: brightpurple[100],
            subtleHovered: brightpurple[500],
            subtlePressed: brightpurple[600],
            subtleDisabled: brightpurple[100],
            bold: brightpurple[600],
            boldHovered: brightpurple[700],
            boldPressed: brightpurple[800],
            boldDisabled: brightpurple[100],
        },
        success: {
            subtle: green[300],
            subtleHovered: green[500],
            subtlePressed: green[500],
            subtleDisabled: green[100],
            bold: green[500],
            boldHovered: green[700],
            boldPressed: green[900],
            boldDisabled: green[300],
        },
        warning: {
            subtle: orange[300],
            subtleHovered: orange[500],
            subtlePressed: orange[500],
            subtleDisabled: orange[100],
            bold: orange[500],
            boldHovered: orange[700],
            boldPressed: orange[900],
            boldDisabled: orange[300],
        },
        danger: {
            subtle: red[100],
            subtleHovered: red[300],
            subtlePressed: red[500],
            subtleDisabled: red[100],
            bold: red[700],
            boldHovered: red[600],
            boldPressed: red[900],
            boldDisabled: red[300],
        },
        info: {
            subtle: blue[300],
            subtleHovered: blue[500],
            subtlePressed: blue[500],
            subtleDisabled: blue[100],
            bold: blue[500],
            boldHovered: blue[700],
            boldPressed: blue[900],
            boldDisabled: blue[300],
        },
    },
    shadow: {
        base: transparent[50],
        strong: transparent[200],
        stronger: transparent[300],
        strongest: transparent[700],
    },
    text: {
        gray: {
            primary: gray[900],
            primaryHovered: gray[900],
            primaryPressed: gray[900],
            primaryDisabled: gray[400],
            secondary: gray[600],
            secondaryHovered: gray[700],
            secondaryPressed: gray[800],
            secondaryDisabled: gray[400],
            tertiary: gray[400],
            tertiaryHovered: gray[500],
            tertiaryPressed: gray[600],
            tertiaryDisabled: gray[300],
        },
        inverse: {
            primary: gray[50],
            primaryHovered: gray[200],
            primaryPressed: gray[300],
            primaryDisabled: gray[400],
        },
        brand: {
            primary: brightpurple[600],
            primaryHovered: brightpurple[700],
            primaryPressed: brightpurple[900],
            primaryDisabled: brightpurple[100],
        },
        success: {
            primary: green[500],
            primaryHovered: green[700],
            primaryPressed: green[900],
            primaryDisabled: green[300],
        },
        warning: {
            primary: orange[500],
            primaryHovered: orange[700],
            primaryPressed: orange[900],
            primaryDisabled: orange[300],
        },
        danger: {
            primary: red[600],
            primaryHovered: red[700],
            primaryPressed: red[900],
            primaryDisabled: red[300],
        },
        info: {
            primary: blue[500],
            primaryHovered: blue[700],
            primaryPressed: blue[900],
            primaryDisabled: blue[300],
        },
    },
    border: {
        neutral: {
            subtlest: gray[200],
            subtlestHovered: gray[300],
            subtlestPressed: gray[400],
            subtlestDisabled: gray[100],
            subtle: gray[300],
            subtleHovered: gray[400],
            subtlePressed: gray[500],
            subtleDisabled: gray[200],
            bold: gray[700],
            boldHovered: gray[800],
            boldPressed: gray[900],
            boldDisabled: gray[300],
        },
        brand: {
            subtlest: brightpurple[100],
            subtlestHovered: brightpurple[500],
            subtlestPressed: brightpurple[600],
            subtlestDisabled: brightpurple[100],
            subtle: brightpurple[500],
            subtleHovered: brightpurple[600],
            subtlePressed: brightpurple[700],
            subtleDisabled: brightpurple[100],
            bold: brightpurple[700],
            boldHovered: brightpurple[900],
            boldPressed: brightpurple[900],
            boldDisabled: brightpurple[100],
        },
        success: {
            subtlest: green[300],
            subtlestHovered: green[500],
            subtlestPressed: green[700],
            subtlestDisabled: green[100],
            subtle: green[500],
            subtleHovered: green[700],
            subtlePressed: green[900],
            subtleDisabled: green[100],
            bold: green[700],
            boldHovered: green[900],
            boldPressed: green[900],
            boldDisabled: green[300],
        },
        warning: {
            subtlest: orange[300],
            subtlestHovered: orange[500],
            subtlestPressed: orange[700],
            subtlestDisabled: orange[100],
            subtle: orange[500],
            subtleHovered: orange[700],
            subtlePressed: orange[900],
            subtleDisabled: orange[100],
            bold: orange[700],
            boldHovered: orange[900],
            boldPressed: orange[900],
            boldDisabled: orange[300],
        },
        danger: {
            subtlest: red[300],
            subtlestHovered: red[500],
            subtlestPressed: red[700],
            subtlestDisabled: red[100],
            subtle: red[500],
            subtleHovered: red[700],
            subtlePressed: red[900],
            subtleDisabled: red[100],
            bold: red[600],
            boldHovered: red[900],
            boldPressed: red[900],
            boldDisabled: red[300],
        },
        info: {
            subtlest: blue[300],
            subtlestHovered: blue[500],
            subtlestPressed: blue[700],
            subtlestDisabled: blue[100],
            subtle: blue[500],
            subtleHovered: blue[700],
            subtlePressed: blue[900],
            subtleDisabled: blue[100],
            bold: blue[700],
            boldHovered: blue[900],
            boldPressed: blue[900],
            boldDisabled: blue[300],
        },
    },
    icon: {
        gray: {
            subtlest: gray[400],
            subtlestHovered: gray[500],
            subtlestPressed: gray[600],
            subtlestDisabled: gray[300],
            subtle: gray[500],
            subtleHovered: gray[600],
            subtlePressed: gray[700],
            subtleDisabled: gray[300],
            bold: gray[700],
            boldHovered: gray[800],
            boldPressed: gray[900],
            boldDisabled: gray[400],
        },
        brand: {
            subtlest: brightpurple[500],
            subtlestHovered: brightpurple[600],
            subtlestPressed: brightpurple[700],
            subtlestDisabled: brightpurple[100],
            primary: brightpurple[600],
            primaryHovered: brightpurple[700],
            primaryPressed: brightpurple[900],
            primaryDisabled: brightpurple[100],
        },
        success: {
            subtlest: green[500],
            subtlestHovered: green[700],
            subtlestPressed: green[900],
            subtlestDisabled: green[300],
            subtle: green[700],
            subtleHovered: green[900],
            subtlePressed: green[900],
            subtleDisabled: green[300],
            bold: green[900],
            boldHovered: green[900],
            boldPressed: green[900],
            boldDisabled: green[300],
        },
        warning: {
            subtlest: orange[500],
            subtlestHovered: orange[700],
            subtlestPressed: orange[900],
            subtlestDisabled: orange[300],
            subtle: orange[700],
            subtleHovered: orange[900],
            subtlePressed: orange[900],
            subtleDisabled: orange[300],
            bold: orange[900],
            boldHovered: orange[900],
            boldPressed: orange[900],
            boldDisabled: orange[300],
        },
        danger: {
            subtlest: red[500],
            subtlestHovered: red[700],
            subtlestPressed: red[900],
            subtlestDisabled: red[300],
            subtle: red[700],
            subtleHovered: red[900],
            subtlePressed: red[900],
            subtleDisabled: red[300],
            bold: red[900],
            boldHovered: red[900],
            boldPressed: red[900],
            boldDisabled: red[300],
        },
        info: {
            subtlest: blue[500],
            subtlestHovered: blue[700],
            subtlestPressed: blue[900],
            subtlestDisabled: blue[300],
            subtle: blue[700],
            subtleHovered: blue[900],
            subtlePressed: blue[900],
            subtleDisabled: blue[300],
            bold: blue[900],
            boldHovered: blue[900],
            boldPressed: blue[900],
            boldDisabled: blue[300],
        },
    },
};
